import { BrandMark } from "./BrandMark";
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from "react";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { authCallbackURL } from "./auth-callback";
import { AppShell, readAppView, type AppView } from "./AppShell";
import { ProjectsIndex } from "./ProjectsIndex";
import { EditableName } from "./EditableName";
import { ConfirmAction } from "./ConfirmAction";
import type { ProjectMetrics } from "../../src/cloud/project-index";
import { FolderGit2, ArrowLeft, ArrowRight, Settings } from "lucide-react";
import { AccountSetup, readAccountFlow, type ComputerApprovalState, type AccountSubscriptionState } from "./AccountSetup";
import { ResultsProject } from "./ResultsProject";

type Project = {
  metrics?: ProjectMetrics;
  displayName?: string | null;
  projectId: string;
  sourceDomainId: string;
  linkedAt: string;
  lastSyncedAt: string | null;
};
type Account = {
  accountId: string;
  devices?: Array<{
    deviceId: string;
    deviceLabel?: string;
    label?: string;
    status?: "active" | "revoked";
    enrolledAt?: string;
    revokedAt?: string | null;
  }>;
  entitlementStatus?: string;
};
type ViewState =
  "signedOut" | "ready" | "loading" | "error" | "offline" | "revoked";
type SupabaseConfig = {
  supabaseUrl: string;
  supabaseAnonKey: string;
  authCallbackURL?: string;
};
const API = "/api/cloud/v1";
const time = (value?: string | null) => value ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "Unknown";
const compactId = (value: string) =>
  value.length > 16 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value;
function credentialFromExistingConfiguration(
  value: unknown,
  expected: { accountId: string; deviceId: string; endpoint: string },
): string {
  if (
    !isObject(value) ||
    Object.keys(value).sort().join(",") !==
      "accountId,credential,deviceId,endpoint,schema"
  )
    throw Error("invalid_configuration");
  if (
    value.schema !== "phasedrift-collector/v1" ||
    value.accountId !== expected.accountId ||
    value.deviceId !== expected.deviceId ||
    value.endpoint !== expected.endpoint ||
    typeof value.credential !== "string" ||
    !/^[A-Za-z0-9_-]{43}$/.test(value.credential)
  )
    throw Error("invalid_configuration");
  return value.credential;
}
const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);
const tokenKey = "phasedrift.cloud.access-token";
const token = () =>
  typeof sessionStorage === "undefined"
    ? null
    : sessionStorage.getItem(tokenKey);
const failureState = (error: unknown): ViewState => {
  const message = error instanceof Error ? error.message : "error";
  if (message === "signed_out") return "signedOut";
  return message === "offline" || message === "revoked" ? message : "error";
};
async function cloud<T>(path: string, signal?: AbortSignal): Promise<T> {
  const accessToken = token();
  const response = await fetch(`${API}${path}`, {
    signal: signal ? AbortSignal.any([signal,AbortSignal.timeout(20000)]) : AbortSignal.timeout(20000),
    credentials: "same-origin",
    headers: {
      accept: "application/json",
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
  });
  if (response.status === 401) throw new Error("signed_out");
  if (response.status === 403) throw new Error("revoked");
  if (!response.ok)
    throw new Error(response.status === 503 ? "offline" : "request_failed");
  return response.json() as Promise<T>;
}
type SupabaseConnection = {
  readonly client: SupabaseClient;
  readonly config: SupabaseConfig;
};
let clientPromise: Promise<SupabaseConnection> | null = null;
async function publicConfig(): Promise<SupabaseConfig> {
  const response = await fetch(`${API}/config`, {
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new Error("offline");
  const config = (await response.json()) as Partial<SupabaseConfig>;
  if (!config.supabaseUrl || !config.supabaseAnonKey) throw new Error("error");
  return config as SupabaseConfig;
}
function supabase(): Promise<SupabaseConnection> {
  clientPromise ??= publicConfig().then((config) => ({
    config,
    client: createClient(config.supabaseUrl, config.supabaseAnonKey, {
      auth: {
        flowType: "pkce",
        detectSessionInUrl: true,
        persistSession: true,
      },
    }),
  }));
  return clientPromise;
}
async function beginSignIn() {
  const { client, config } = await supabase();
  const { error } = await client.auth.signInWithOAuth({
    provider: "github",
    options: { redirectTo: authCallbackURL(config.authCallbackURL, location) },
  });
  if (error) throw error;
}
export function HistoryDashboard() {
  const [accountFlow] = useState(readAccountFlow);
  const [approvalState, setApprovalState] = useState<ComputerApprovalState>(accountFlow.device ? "pending" : null);
  const [view, setView] = useState<AppView>(readAppView);
  const [recovery, setRecovery] = useState(false);
  const [authError, setAuthError] = useState("");
  const emailVerification = useRef<Promise<{ error: unknown }> | null>(null);
  const [legacyEmailLogin] = useState(
    () => new URLSearchParams(location.search).get("type") === "magiclink",
  );
  useEffect(() => {
    if (legacyEmailLogin)
      setAuthError(
        "Sign in with email and password. Use Set or reset password if you previously used an email code.",
      );
  }, [legacyEmailLogin]);
  const [emailCallback] = useState(() => {
    const url = new URL(location.href),
      hash = url.searchParams.get("token_hash"),
      type = url.searchParams.get("type");
    if (hash) {
      url.searchParams.delete("token_hash");
      url.searchParams.delete("type");
      history.replaceState(history.state, "", url);
    }
    return hash &&
      ["signup", "recovery", "invite", "email_change"].includes(type ?? "")
      ? {
          hash,
          type: type as "signup" | "recovery" | "invite" | "email_change",
        }
      : null;
  });
  const navigate = (next: AppView) => {
    const url = new URL(location.href);
    url.searchParams.set("view", next);
    url.searchParams.delete("project");
    if(next === "projects") setProjectId("");
    history.pushState(history.state, "", url);
    setView(next);
  };
  useEffect(() => {
    const changed = () => { setView(readAppView()); setProjectId(new URLSearchParams(location.search).get("project") ?? ""); };
    addEventListener("popstate", changed);
    return () => removeEventListener("popstate", changed);
  }, []);

  const [theme, setTheme] = useState<"dark" | "light">(() =>
    typeof matchMedia !== "undefined" &&
    matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
  );
  const [state, setState] = useState<ViewState>(() =>
    token() ||
    (typeof location !== "undefined" &&
      new URLSearchParams(location.search).has("code"))
      ? "loading"
      : "signedOut",
  );
  const [subscriptionState, setSubscriptionState] = useState<AccountSubscriptionState | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [authSubject, setAuthSubject] = useState<string | null | undefined>();
  const activeSubject = useRef<string | null>(null);
  const authEpoch = useRef(0);
  const ownerEpoch = authEpoch.current;
  const accountLoad = useRef(0);
  const currentOwner = () =>
    Boolean(
      authSubject &&
      activeSubject.current === authSubject &&
      authEpoch.current === ownerEpoch,
    );
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState(() => new URLSearchParams(location.search).get("project") ?? "");
  const [deleteProjectId, setDeleteProjectId] = useState("");
  const [confirmDeviceId, setConfirmDeviceId] = useState("");
  const [deletionStatus, setDeletionStatus] = useState<"checking"|"allowed"|"subscription"|"unavailable">("checking");
  const [refreshingProjects, setRefreshingProjects] = useState(false);
  const [message, setMessage] = useState("");
  const [confirm, setConfirm] = useState<
    "delete" | "accountDelete" | "revoke" | "replace" | null
  >(null);
  const [deviceLabel, setDeviceLabel] = useState("");
  const [machineCode, setMachineCode] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [enrollmentNote, setEnrollmentNote] = useState("");
  const [devicePending, setDevicePending] = useState("");
  const [replaceFor, setReplaceFor] = useState<string | null>(null);
  const [replacementLabel, setReplacementLabel] = useState("");
  const [deviceNote, setDeviceNote] = useState("");
  const [pairingDeviceId, setPairingDeviceId] = useState("");
  const [pairingIntent, setPairingIntent] = useState<
    "owner_confirmed_pairing" | "owner_confirmed_restore"
  >("owner_confirmed_pairing");
  const [pairingBusy, setPairingBusy] = useState(false);
  const [pairingNote, setPairingNote] = useState("");
  const [issuedCredential, setIssuedCredential] = useState<{
    device: { deviceId: string; deviceLabel?: string; label?: string };
    credential: string;
  } | null>(null);
  const [pairingAwaitingConfig, setPairingAwaitingConfig] = useState<{
    deviceId: string;
    projectId: string;
    sourceDomainId: string;
    pairing: "owner_confirmed_pairing" | "owner_confirmed_restore";
  } | null>(null);
  const existingCollectorFile = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    void supabase()
      .then(async ({ client }) => {
        // The SDK owns the one-use PKCE exchange and removes its URL parameters.
        // A second exchange (including React StrictMode's effect replay) fails.
        const { error } = await client.auth.initialize();
        if (emailCallback) {
          emailVerification.current ??= client.auth.verifyOtp({
            token_hash: emailCallback.hash,
            type: emailCallback.type,
          });
          const result = await emailVerification.current;
          if (!active) return;
          if (result.error) {
            setAuthError(
              "This email link expired or has already been used. Request a new link below.",
            );
          } else if (["recovery", "invite"].includes(emailCallback.type))
            setRecovery(true);
        }
        const { data: saved } = await client.auth.getSession();
        if (error && !saved.session) throw error;
        if (!active) return;
        if (error && saved.session) {
          const url = new URL(location.href);
          url.searchParams.delete("code");
          history.replaceState(history.state, "", url);
        }
        const { data } = client.auth.onAuthStateChange((_event, session) => {
          if (!active) return;
          if (_event === "PASSWORD_RECOVERY") setRecovery(true);
          if (session) sessionStorage.setItem(tokenKey, session.access_token);
          else sessionStorage.removeItem(tokenKey);
          if (activeSubject.current !== (session?.user.id ?? null))
            authEpoch.current++;
          activeSubject.current = session?.user.id ?? null;
          setAuthSubject(session?.user.id ?? null);
        });
        unsubscribe = () => data.subscription.unsubscribe();
      })
      .catch((error) => {
        if (active) setState(failureState(error));
      });
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (authSubject === undefined) return;
    if (authSubject) void load();
    else {
      accountLoad.current++;
      setAccount(null);
      setProjects([]);
      setProjectId("");
      setConfirm(null);
      setMessage("");
      setDeviceLabel("");
      setMachineCode("");
      setIssuedCredential(null);
      setPairingAwaitingConfig(null);
      setPairingDeviceId("");
      setPairingNote("");
      setPairingBusy(false);
      setReplaceFor(null);
      setReplacementLabel("");
      setDevicePending("");
      setDeviceNote("");
      setEnrolling(false);
      setEnrollmentNote("");
      sessionStorage.removeItem("phasedrift.cloud.large-export");
      setState("signedOut");
    }
  }, [authSubject]);

  const startSignIn = () => {
    if (token()) signOut();
    else document.querySelector<HTMLInputElement>("#account-email")?.focus();
  };
  const signOut = () =>
    void supabase()
      .then(async ({ client }) => {
        const { error } = await client.auth.signOut({ scope: "local" });
        if (error) throw error;
        if (approvalState === "approved") setApprovalState(null);
      })
      .catch(() => setMessage("Sign out was not confirmed. Try again."));

  const load = async (background = false) => {
    const requestId = ++accountLoad.current;
    if (!background) { setState("loading"); setMessage(""); }
    try {
      const [me, projectResponse] = await Promise.all([
        cloud<Account>("/me"),
        cloud<{ projects: Project[] }>("/projects"),
      ]);
      if (requestId !== accountLoad.current || !currentOwner()) return;
      setAccount(me);
      setProjects(projectResponse.projects);
      setProjectId(
        (current) => projectResponse.projects.some(project => project.projectId === current) ? current : "",
      );
      setState("ready");
    } catch (error) {
      if (requestId === accountLoad.current && currentOwner()) {
        const failed = failureState(error);
        if (!background || failed === 'signedOut' || failed === 'revoked') setState(failed);
      }
    }
  };
  const openProject = (id: string) => {
    const url=new URL(location.href);url.searchParams.set("view","projects");url.searchParams.set("project",id);
    history.pushState(history.state,"",url);setView("projects");setProjectId(id);
  };
  const refreshProjects = async () => { setRefreshingProjects(true); try { await load(true); } finally { setRefreshingProjects(false); } };
  const renameProject = async (id: string, displayName: string) => {
    const response=await fetch(`${API}/projects/${encodeURIComponent(id)}/name`,{method:"POST",signal:AbortSignal.timeout(20000),
      headers:{"content-type":"application/json",authorization:`Bearer ${token()}`},body:JSON.stringify({displayName})});
    if(!response.ok || !currentOwner())throw Error("rename_failed");
    setProjects(items=>items.map(project=>project.projectId===id?{...project,displayName}:project));
  };
  useEffect(() => {
    let active=true;
    setDeletionStatus("checking");
    if(view==="settings" && authSubject && account)void cloud<{allowed:boolean}>("/account/deletion")
      .then(value=>{if(active && currentOwner())setDeletionStatus(value.allowed===true?"allowed":"subscription");})
      .catch(()=>{if(active && currentOwner())setDeletionStatus("unavailable");});
    return ()=>{active=false;};
  },[view,authSubject,account?.accountId]);
  const selectedProject = projects.find(
    (project) => project.projectId === projectId,
  );
  const deleteAccount = async () => {
    setConfirm(null);
    try {
      const response = await fetch(`${API}/account`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: {"content-type":"application/json",...(token() ? { authorization: `Bearer ${token()}` } : {})},
        body: JSON.stringify({confirmation:"DELETE FULL ACCOUNT"}),
      });
      if (!currentOwner()) return;
      if (response.status === 401) throw Error("signed_out");
      if (response.status === 403) throw Error("revoked");
      if(response.status===409){setDeletionStatus("subscription");setMessage("Cancel your subscription and wait until cancellation is complete before deleting your cloud account.");return;}
      if (!response.ok)
        throw Error(response.status === 503 ? "offline" : "request_failed");
      setAccount(null);
      setProjects([]);
      setProjectId("");
      setMessage(
        "Cloud account deletion has started. Collector access is revoked while saved cloud history is purged.",
      );
    } catch (error) {
      if (!currentOwner()) return;
      const view = failureState(error);
      if (view === "revoked" || view === "signedOut") setState(view);
      else
        setMessage(
          "Account deletion could not be confirmed. No local analysis was started.",
        );
    }
  };
  const approvePairing = async () => {
    if (!selectedProject || !pairingDeviceId) return;
    setPairingBusy(true);
    setPairingNote("");
    try {
      const response = await fetch(
        `${API}/projects/${encodeURIComponent(selectedProject.projectId)}`,
        {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "content-type": "application/json",
            ...(token() ? { authorization: `Bearer ${token()}` } : {}),
          },
          body: JSON.stringify({
            sourceDomainId: selectedProject.sourceDomainId,
            deviceId: pairingDeviceId,
            pairing: pairingIntent,
          }),
        },
      );
      if (!currentOwner()) return;
      if (response.status === 401) throw Error("signed_out");
      if (response.status === 403) throw Error("revoked");
      if (!response.ok) throw Error("request_failed");
      const issued =
        issuedCredential?.device.deviceId === pairingDeviceId
          ? issuedCredential
          : null;
      if (issued) {
        downloadCollectorConfiguration(issued.device, issued.credential, {
          projectId: selectedProject.projectId,
          sourceDomainId: selectedProject.sourceDomainId,
          pairing: pairingIntent,
        });
        setIssuedCredential(null);
        setPairingAwaitingConfig(null);
        setPairingNote(
          "Collector mapping confirmed. Download the replacement private configuration and import it locally; sync consent is unchanged.",
        );
      } else {
        setPairingAwaitingConfig({
          deviceId: pairingDeviceId,
          projectId: selectedProject.projectId,
          sourceDomainId: selectedProject.sourceDomainId,
          pairing: pairingIntent,
        });
        setPairingNote(
          "Collector mapping confirmed. Choose that collector’s existing private configuration to create its pairing update locally.",
        );
      }
    } catch (error) {
      if (!currentOwner()) return;
      const view = failureState(error);
      if (view === "revoked" || view === "signedOut") setState(view);
      else setPairingNote("Collector mapping was not confirmed.");
    } finally {
      if (currentOwner()) setPairingBusy(false);
    }
  };
  const enrichExistingConfiguration = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected || !pairingAwaitingConfig || !account) return;
    try {
      if (selected.size > 4096) throw Error("invalid_configuration");
      const credential = credentialFromExistingConfiguration(
        JSON.parse(await selected.text()),
        {
          accountId: account.accountId,
          deviceId: pairingAwaitingConfig.deviceId,
          endpoint: window.location.origin,
        },
      );
      if (!currentOwner()) return;
      downloadCollectorConfiguration(
        { deviceId: pairingAwaitingConfig.deviceId },
        credential,
        pairingAwaitingConfig,
      );
      setPairingAwaitingConfig(null);
      setPairingNote(
        "Pairing configuration downloaded. Import it locally; sync consent is unchanged.",
      );
    } catch {
      if (!currentOwner()) return;
      setPairingNote(
        "That private configuration does not match this account and collector. No configuration was downloaded.",
      );
    }
  };
  const deleteProject = async () => {
    setConfirm(null);
    try {
      const response = await fetch(
        `${API}/projects/${encodeURIComponent(deleteProjectId)}`,
        {
          method: "DELETE",
          credentials: "same-origin",
          headers: {"content-type":"application/json",...(token() ? { authorization: `Bearer ${token()}` } : {})},
          body: JSON.stringify({confirmation:"DELETE PROJECT"}),
        },
      );
      if (!currentOwner()) return;
      if (response.status === 401) throw Error("signed_out");
      if (response.status === 403) throw Error("revoked");
      if (!response.ok) throw Error("request_failed");
      setProjects((items) =>
        items.filter((item) => item.projectId !== deleteProjectId),
      );
      if(projectId===deleteProjectId)setProjectId("");
      setMessage("Project history was deleted.");
    } catch (error) {
      if (!currentOwner()) return;
      const view = failureState(error);
      if (view === "revoked" || view === "signedOut") setState(view);
      else
        setMessage(
          "Project deletion did not finish. No local analysis was started.",
        );
    }
  };
  const downloadCollectorConfiguration = (
    device: { deviceId: string; deviceLabel?: string; label?: string },
    credential: string,
    pairing?: {
      projectId: string;
      sourceDomainId: string;
      pairing: "owner_confirmed_pairing" | "owner_confirmed_restore";
    },
  ) => {
    if (!account || !currentOwner()) return;
    const configuration = {
      schema: "phasedrift-collector/v1",
      endpoint: window.location.origin,
      accountId: account.accountId,
      deviceId: device.deviceId,
      credential,
      ...(pairing ?? {}),
    };
    const blob = new Blob([JSON.stringify(configuration, null, 2)], {
        type: "application/json",
      }),
      link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `phasedrift-collector-${compactId(device.deviceId)}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  };
  const connectComputer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const label = deviceLabel.trim(),
      accessToken = token();
    if (!label || !accessToken || !account) return;
    setEnrolling(true);
    setEnrollmentNote("");
    try {
      const response = await fetch(`${API}/devices/enroll`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          authorization: `Bearer ${accessToken}`,
          "idempotency-key": crypto.randomUUID(),
        },
        body: JSON.stringify({
          deviceLabel: label,
          machineCode: machineCode.trim(),
        }),
      });
      const result = (await response.json()) as {
        device?: {
          deviceId: string;
          deviceLabel?: string;
          status?: "active" | "revoked";
        };
        credential?: string | null;
        outcome?: "issued" | "replayed";
        message?: string;
        error?: string;
      };
      if (!currentOwner()) return;
      if (!response.ok || !result.device)
        throw Error(
          result.message ??
            result.error ??
            "Computer enrollment did not complete",
        );
      if (!result.credential) {
        setEnrollmentNote(
          "This enrollment was already issued. Its private configuration cannot be downloaded again; replace or revoke the device to issue a new one.",
        );
        return;
      }
      downloadCollectorConfiguration(result.device, result.credential);
      setMachineCode("");
      setIssuedCredential({
        device: result.device,
        credential: result.credential,
      });
      setAccount((current) =>
        current
          ? {
              ...current,
              devices: [
                ...(current.devices ?? []),
                {
                  deviceId: result.device!.deviceId,
                  deviceLabel: result.device!.deviceLabel ?? label,
                  status: result.device!.status ?? "active",
                },
              ],
            }
          : current,
      );
      setDeviceLabel("");
      setEnrollmentNote(
        "Private collector configuration downloaded. Import it in the local PhaseDrift app on that computer.",
      );
    } catch (error) {
      if (!currentOwner()) return;
      setEnrollmentNote(
        error instanceof Error
          ? error.message
          : "Computer enrollment did not complete.",
      );
    } finally {
      if (currentOwner()) setEnrolling(false);
    }
  };
  const revokeDevice = async (deviceId: string) => {
    const accessToken = token();
    if (!accessToken) return setState("signedOut");
    setDevicePending(deviceId);
    setDeviceNote("");
    try {
      const response = await fetch(`${API}/devices/revoke`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ deviceId, confirmation:"REVOKE COMPUTER" }),
      });
      if (response.status === 401) throw Error("signed_out");
      if (response.status === 403) throw Error("revoked");
      const device = (await response.json()) as {
        deviceId: string;
        status: "active" | "revoked";
        deviceLabel?: string;
        label?: string;
      };
      if (!currentOwner()) return;
      if (!response.ok || !device.deviceId) throw Error("request_failed");
      setAccount((current) =>
        current
          ? {
              ...current,
              devices: (current.devices ?? []).map((item) =>
                item.deviceId === deviceId
                  ? { ...item, ...device, status: "revoked" }
                  : item,
              ),
            }
          : current,
      );
      setDeviceNote(
        "Computer released. Its license and history credentials cannot make new online requests.",
      );
    } catch (error) {
      if (!currentOwner()) return;
      const view = failureState(error);
      if (view === "signedOut" || view === "revoked") setState(view);
      else setDeviceNote("Device revocation was not confirmed.");
    } finally {
      if (currentOwner()) setDevicePending("");
    }
  };
  const replaceDevice = async () => {
    const accessToken = token(),
      oldDeviceId = replaceFor,
      deviceLabel = replacementLabel.trim();
    if (!accessToken) return setState("signedOut");
    if (!oldDeviceId || !deviceLabel) return;
    setDevicePending(oldDeviceId);
    setDeviceNote("");
    try {
      const response = await fetch(`${API}/devices/replace`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          authorization: `Bearer ${accessToken}`,
          "idempotency-key": crypto.randomUUID(),
        },
        body: JSON.stringify({
          oldDeviceId,
          confirmation:"REPLACE COMPUTER",
          deviceLabel,
          machineCode: machineCode.trim(),
        }),
      });
      if (response.status === 401) throw Error("signed_out");
      if (response.status === 403) throw Error("revoked");
      const result = (await response.json()) as {
        device?: {
          deviceId: string;
          deviceLabel?: string;
          label?: string;
          status?: "active" | "revoked";
        };
        credential?: string | null;
      };
      if (!currentOwner()) return;
      if (!response.ok || !result.device) throw Error("request_failed");
      if (!result.credential) {
        setDeviceNote(
          "This replacement was already issued. Its private configuration cannot be downloaded again.",
        );
        return;
      }
      downloadCollectorConfiguration(result.device, result.credential);
      setMachineCode("");
      setIssuedCredential({
        device: result.device,
        credential: result.credential,
      });
      setAccount((current) =>
        current
          ? {
              ...current,
              devices: [
                ...(current.devices ?? []).map((item) =>
                  item.deviceId === oldDeviceId
                    ? { ...item, status: "revoked" as const }
                    : item,
                ),
                {
                  deviceId: result.device!.deviceId,
                  deviceLabel: result.device!.deviceLabel ?? deviceLabel,
                  status: result.device!.status ?? "active",
                },
              ],
            }
          : current,
      );
      setReplaceFor(null);
      setReplacementLabel("");
      setDeviceNote(
        "Replacement configuration downloaded. Import it on the replacement computer.",
      );
    } catch (error) {
      if (!currentOwner()) return;
      const view = failureState(error);
      if (view === "signedOut" || view === "revoked") setState(view);
      else setDeviceNote("Replacement was not confirmed.");
    } finally {
      if (currentOwner()) setDevicePending("");
    }
  };

  const authenticated = Boolean(authSubject) && !recovery;
  const subscription = subscriptionState && subscriptionState.subject === authSubject ? subscriptionState.subscription : null;
  const subscriptionActive = subscription?.linked === true && subscription.status === "active";
  const computerConnected = account?.devices?.some(device => device.status === 'active') === true;
  const awaitingProject = computerConnected && projects.length === 0;
  const awaitingMeasurements = projects.some(project => project.lastSyncedAt === null);
  useEffect(() => {
    if (!authenticated || !subscriptionActive || approvalState || view !== 'projects' || (!awaitingProject && !awaitingMeasurements)) return;
    let stopped = false, checking = false, polls = 0, lastCheck = Date.now();
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => { timer = setTimeout(() => void check(), polls < 20 ? 15000 : 60000); };
    const check = async () => {
      if (stopped || checking) return;
      clearTimeout(timer);
      if (document.visibilityState === 'visible') {
        checking = true; lastCheck = Date.now(); polls++;
        try { await load(true); } finally { checking = false; }
      }
      if (!stopped) schedule();
    };
    const focus = () => { if (document.visibilityState === 'visible' && Date.now() - lastCheck >= 5000) void check(); };
    schedule(); window.addEventListener('focus', focus); document.addEventListener('visibilitychange', focus);
    return () => { stopped = true; clearTimeout(timer); window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', focus); };
  }, [authSubject, authenticated, subscriptionActive, approvalState, view, awaitingProject, awaitingMeasurements]);
  const content = (
    <main
      className={authenticated ? approvalState ? "approval-main" : "history-main" : "auth-main"}
      aria-busy={authSubject === undefined}
    >
      {authError && (
        <p className="auth-callback-error" role="alert">
          {authError}
        </p>
      )}
      <AccountSetup
        flow={accountFlow}
        approvalState={approvalState}
        onApprovalStateChange={setApprovalState}
        subject={authSubject}
        view={view}
        recovery={recovery}
        onPasswordSaved={() => {
          setRecovery(false);
          setView("projects");
          setAuthError("");
        }}
        navigate={navigate}
        getClient={async () => (await supabase()).client}
        onConnected={() => void load()}
        onSubscriptionChange={setSubscriptionState}
      />
      {authenticated && !approvalState && subscriptionActive && view === "projects" && (
        <>
          <section className={`page-intro ${selectedProject ? "has-project" : ""}`}>
            <div>
              <h1>Your projects.</h1>
              <p>Code health and agent work, connected over time.</p>
            </div>
            {!awaitingProject && <button
              className="primary-button"
              onClick={() => navigate("setup")}
            >
              Connect a project <ArrowRight size={16} />
            </button>}
          </section>
          {state === "loading" && <Loading />}
          {state === "offline" && (
            <StateCard
              title="Cloud history is unavailable"
              body="Your saved local work is unaffected. Try again to reload the account or history."
              action={
                <button className="primary-button" onClick={() => void load()}>
                  Try again
                </button>
              }
            />
          )}
          {state === "revoked" && (
            <StateCard
              title="This account no longer has access"
              body="Ask the account owner to restore access, then sign in again."
              action={
                <button className="secondary-button" onClick={startSignIn}>
                  Sign in again
                </button>
              }
            />
          )}
          {state === "error" && (
            <StateCard
              title="We could not load cloud history"
              body="No analysis or collection was started."
              action={
                <button className="primary-button" onClick={() => void load()}>
                  Try again
                </button>
              }
            />
          )}
          {state === "ready" && (
            <>
              {awaitingProject ? (
                <section className="setup-progress" aria-labelledby="setup-progress-title">
                  <h2 id="setup-progress-title">Your computer is connected.</h2>
                  <p>Let your agent finish connecting the project. It will appear here when registered, followed by its first synced measurements.</p>
                  <ol aria-label="Project connection progress">
                    <li>Computer authorized</li>
                    <li aria-current="step">Waiting for your project</li>
                    <li>First measurements synced</li>
                  </ol>
                  <p>This page updates automatically. We haven’t received a project yet; we can’t see your agent’s local setup progress.</p>
                  <button className="quiet-link" onClick={() => navigate('computers')}>View connected computers</button>
                </section>
              ) : projects.length === 0 ? (
                <StateCard
                  title="Your first project starts here."
                  body="Let your agent connect a repository. Work as usual: PhaseDrift tracks code health and agent friction locally. Enable history sync to bring your measurements here."
                  action={
                    <button
                      className="primary-button"
                      onClick={() => navigate("setup")}
                    >
                      Set up a project <ArrowRight size={16} />
                    </button>
                  }
                />
              ) : !selectedProject ? (
                <ProjectsIndex projects={projects} openProject={openProject} refresh={() => void refreshProjects()} refreshing={refreshingProjects} />
              ) : (
                <>
                  <div className="project-detail-heading"><button type="button" className="quiet-link" onClick={() => navigate("projects")}><ArrowLeft size={16} aria-hidden="true" />All projects</button>
                    <button type="button" className="secondary-button" onClick={() => navigate("settings")}><Settings size={16} aria-hidden="true" />Project settings</button></div>
                  {selectedProject.lastSyncedAt === null && <p role="status">Project connected. Waiting for its first synced measurements; this page updates automatically.</p>}
                  <ResultsProject key={selectedProject.projectId} projectId={selectedProject.projectId}
                    projectName={selectedProject.displayName || "Project results"} lastSyncedAt={selectedProject.lastSyncedAt}
                    request={cloud} theme={theme} onRefreshAccount={() => void load(true)} />
                </>
              )}
            </>
          )}
        </>
      )}
      {authenticated && !approvalState && view === "settings" && state === "ready" && (
        <section className="history-account-settings">
          <h2>Cloud history</h2>
          <p className="settings-intro">
            Sync stays optional. Manage collectors and saved cloud data here.
          </p>
          <section className="project-settings" aria-labelledby="project-settings-title">
            <div className="panel-heading"><div><h2 id="project-settings-title">Projects</h2><p>Choose the names shown in your workspace. Repository identities stay the same.</p></div></div>
            {projects.length ? <ul>{projects.map(project => <li key={project.projectId}>
              <div><EditableName value={project.displayName || "Unnamed project"} label={project.displayName || "project"} onSave={name => renameProject(project.projectId,name)} />
                <small className="project-identity">Project {compactId(project.projectId)}</small></div>
              <div className="project-setting-actions"><button type="button" className="secondary-button" aria-label={`Collector settings for ${project.displayName || "project"}`} onClick={() => {
                setProjectId(project.projectId);const url=new URL(location.href);url.searchParams.set("project",project.projectId);history.replaceState(history.state,"",url.pathname+url.search);
                requestAnimationFrame(()=>document.getElementById("devices")?.scrollIntoView({block:"start"}));
              }}>Collector settings</button><button type="button" className="text-danger" onClick={() => {setDeleteProjectId(project.projectId);setConfirm("delete");}}>Delete project</button></div>
            </li>)}</ul> : <p>No projects are connected.</p>}
          </section>
          <section id="devices" className="devices-panel">
            <div className="panel-heading">
              <div>
                <h2>Account devices</h2>
                <p>
                  Browser devices are not collectors. Download a private
                  configuration before connecting a computer.
                </p>
              </div>
            </div>
            {subscriptionActive ? <details className="computer-enrollment">
              <summary>Connect computer</summary>
              <form onSubmit={connectComputer}>
                <p>
                  Activate PhaseDrift on this computer first, then run{" "}
                  <code>phasedrift license connection-code</code>. Paste its
                  private code below. This uses the same machine slot as your
                  license.
                </p>
                <label>
                  Machine connection code
                  <input
                    type="password"
                    autoComplete="off"
                    value={machineCode}
                    onChange={(event) => setMachineCode(event.target.value)}
                    pattern="pdm1_[a-f0-9]{64}"
                    required
                  />
                </label>
                <label>
                  Computer name
                  <input
                    value={deviceLabel}
                    onChange={(event) => setDeviceLabel(event.target.value)}
                    minLength={1}
                    maxLength={240}
                    required
                    placeholder="Work laptop"
                  />
                </label>
                <button
                  className="primary-button"
                  type="submit"
                  disabled={enrolling || !account || !machineCode}
                >
                  {enrolling
                    ? "Preparing download…"
                    : "Download collector configuration"}
                </button>
              </form>
              {enrollmentNote && <p role="status">{enrollmentNote}</p>}
            </details> : <p>{subscription
              ? <>An active subscription is needed to connect a computer. <button className="quiet-link" onClick={() => navigate("billing")}>View subscription</button></>
              : "Check your subscription before connecting a computer."}</p>}
            {account?.devices?.length ? (
              <ul>
                {account.devices.map((device) => (
                  <li key={device.deviceId}>
                    <div>
                      <strong>
                        {subscription?.machines?.devices?.find(item=>item.device_id===device.deviceId)?.label || device.deviceLabel || device.label || "Computer"}
                      </strong>
                      <span>
                        {device.status ?? "Status unknown"} ·{" "}
                        {compactId(device.deviceId)}
                      </span>
                    </div>
                    {device.status !== "revoked" && (
                      <div className="device-actions">
                        <button
                          className="secondary-button"
                          disabled={!!devicePending}
                          onClick={() => {
                            setReplaceFor(device.deviceId);
                            setReplacementLabel(
                              device.deviceLabel ?? device.label ?? "",
                            );
                          }}
                        >
                          Replace
                        </button>
                        <button
                          className="text-danger"
                          disabled={!!devicePending}
                          onClick={() => {setConfirmDeviceId(device.deviceId);setConfirm("revoke");}}
                        >
                          {devicePending === device.deviceId
                            ? "Updating…"
                            : "Revoke"}
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p>Device information is not available for this account.</p>
            )}
            {selectedProject &&
              account?.devices?.some(
                (device) => device.status !== "revoked",
              ) && (
                <details className="computer-enrollment">
                  <summary>Approve collector mapping</summary>
                  <div className="collector-mapping-fields"><p>
                    Confirm a collector for <strong>{selectedProject.displayName || "this project"}</strong>. This maps
                    access only; it does not enable sync.
                  </p>
                  <label>
                    Collector device
                    <select
                      name="collectorDevice"
                      aria-label="Collector device"
                      value={pairingDeviceId}
                      onChange={(event) =>
                        setPairingDeviceId(event.target.value)
                      }
                    >
                      <option value="">Choose a collector</option>
                      {account.devices
                        .filter((device) => device.status !== "revoked")
                        .map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {subscription?.machines?.devices?.find(item=>item.device_id===device.deviceId)?.label || device.deviceLabel || device.label || "Computer"}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label>
                    Mapping purpose
                    <select
                      name="pairingIntent"
                      aria-label="Mapping purpose"
                      value={pairingIntent}
                      onChange={(event) =>
                        setPairingIntent(
                          event.target.value as typeof pairingIntent,
                        )
                      }
                    >
                      <option value="owner_confirmed_pairing">
                        Pair this collector
                      </option>
                      <option value="owner_confirmed_restore">
                        Restore this collector
                      </option>
                    </select>
                  </label>
                  <button
                    className="primary-button"
                    disabled={!pairingDeviceId || pairingBusy}
                    onClick={() => void approvePairing()}
                  >
                    {pairingBusy ? "Confirming…" : "Confirm mapping"}
                  </button>
                  {pairingAwaitingConfig && (
                    <>
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={() => existingCollectorFile.current?.click()}
                      >
                        Choose existing private configuration
                      </button>
                      <input
                        ref={existingCollectorFile}
                        className="sr-only"
                        type="file"
                        accept="application/json,.json"
                        aria-label="Existing private collector configuration"
                        onChange={enrichExistingConfiguration}
                      />
                    </>
                  )}
                  {pairingNote && <p role="status">{pairingNote}</p>}
                  </div>
                </details>
              )}
            {replaceFor && (
              <form className="device-replacement" onSubmit={event => {event.preventDefault();setConfirm("replace");}}>
                <p>
                  Activate the replacement computer first. Paste the code from{" "}
                  <code>phasedrift license connection-code</code>.
                </p>
                <label>
                  Replacement machine connection code
                  <input
                    type="password"
                    autoComplete="off"
                    value={machineCode}
                    onChange={(event) => setMachineCode(event.target.value)}
                    pattern="pdm1_[a-f0-9]{64}"
                    required
                  />
                </label>
                <label>
                  Replacement computer name
                  <input
                    value={replacementLabel}
                    onChange={(event) =>
                      setReplacementLabel(event.target.value)
                    }
                    minLength={1}
                    maxLength={240}
                    required
                  />
                </label>
                <button
                  className="primary-button"
                  disabled={!!devicePending || !machineCode}
                  type="submit"
                >
                  Download replacement configuration
                </button>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setReplaceFor(null)}
                >
                  Cancel
                </button>
              </form>
            )}
            {deviceNote && <p role="status">{deviceNote}</p>}
          </section>
          <section
            className="devices-panel"
            aria-labelledby="account-delete-title"
          >
            <h2 id="account-delete-title">Delete cloud account</h2>
            {deletionStatus!=="allowed" && <p role="status">{deletionStatus==="checking"?"Checking subscription status…":deletionStatus==="subscription"?"Your subscription or checkout is still active. Complete cancellation in Billing before deleting your account.":"Subscription status could not be verified. Deletion is unavailable until we can confirm it."} <button type="button" className="quiet-link" onClick={() => navigate("billing")}>Manage subscription</button></p>}
            <p>
              Permanently remove every cloud project and its saved history, and revoke all collector access.
              Local project files and local history stay on your computers. Billing records are retained as required.
              Cancel your subscription and wait until cancellation is complete before continuing.
            </p>
            <button
              className="danger-button"
              disabled={deletionStatus!=="allowed"}
              onClick={() => setConfirm("accountDelete")}
            >
              Delete cloud account
            </button>
          </section>
        </section>
      )}
    </main>
  );
  return (
    <div className="history-app">
      {authenticated && approvalState ? (
        <>
          <header className="approval-header">
            <a className="history-brand" href="https://phasedrift.ai"><BrandMark />PhaseDrift</a>
            <button className="quiet-link" onClick={signOut}>Sign out</button>
          </header>
          {content}
        </>
      ) : authenticated ? (
        <AppShell
          view={view}
          setupLabel={!subscription ? "Getting started" : !subscriptionActive ? subscription.linked ? "Activate subscription" : "Choose a plan" : undefined}
          navigate={navigate}
          theme={theme}
          toggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")}
          signOut={signOut}
        >
          {content}
        </AppShell>
      ) : (
        content
      )}
      {message && (
        <div className="toast" role="status">
          {message}
        </div>
      )}
      {confirm && <ConfirmAction key={confirm + deleteProjectId + confirmDeviceId}
        title={confirm==="delete"?`Delete ${projects.find(project=>project.projectId===deleteProjectId)?.displayName || "project"}?`:confirm==="accountDelete"?"Delete your entire cloud account?":confirm==="revoke"?"Revoke this computer?":"Replace this computer?"}
        action={confirm==="delete"?"Delete project":confirm==="accountDelete"?"Delete cloud account":confirm==="revoke"?"Revoke computer":"Replace computer"}
        phrase={confirm==="accountDelete"?"DELETE FULL ACCOUNT":confirm==="delete"?"DELETE PROJECT":undefined}
        disabled={confirm==="accountDelete"&&deletionStatus!=="allowed"}
        onCancel={() => setConfirm(null)} onConfirm={async () => {
          if(confirm==="delete")await deleteProject();else if(confirm==="accountDelete")await deleteAccount();
          else if(confirm==="revoke")await revokeDevice(confirmDeviceId);else await replaceDevice();
          setConfirm(null);
        }}>
        <p>{confirm==="delete"?"This permanently removes this project’s cloud history. It cannot be undone. Your local files and local history remain on your computer.":confirm==="accountDelete"?"All cloud projects and their saved history will be permanently deleted. Every collector loses cloud access. This cannot be undone. Your local files remain on your computers. Billing records are retained.":confirm==="revoke"?"This computer will lose license and cloud access for new online requests. Saved cloud history and local files remain. Reconnect it to grant access again.":"The old computer will lose license and cloud access. A new private configuration will be issued for its replacement. Import that configuration on the replacement computer to reconnect."}</p>
      </ConfirmAction>}

    </div>
  );
}

function StateCard({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <section className="state-card">
      <h2>{title}</h2>
      <p>{body}</p>
      {action && <div className="state-actions">{action}</div>}
    </section>
  );
}
function Loading() {
  return (
    <section className="loading-grid" aria-label="Loading cloud history">
      <span />
      <span />
      <span />
    </section>
  );
}
